import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Box, Tooltip, Typography } from '@mui/material';
import React, { FC, useMemo } from 'react';

import {
  DIGITAL_ITEM_ALWAYS_AVAILABLE_MESSAGE,
  PREORDER_IS_ENABLED_FOR_CAMPAIGN_MESSAGE,
} from '../../../constants/inventories';
import { InventoryTypesEnum } from '../../../types/inventories';
import { getItemCountWarning, isItemDigital, isItemOutOfStock } from '../../../utils/inventories';
import { BackorderChip } from './BackorderChip';

interface IProps {
  count?: number;
  isPreorderAllowed?: boolean;
  type: InventoryTypesEnum;
  threshold?: number;
}

export const ItemCountWithWarning: FC<IProps> = ({ count, isPreorderAllowed, type, threshold }) => {
  const isDigitalItem = useMemo(() => isItemDigital({ type }), [type]);

  const tooltipMessage = useMemo(() => {
    if (isDigitalItem) {
      return DIGITAL_ITEM_ALWAYS_AVAILABLE_MESSAGE;
    }
    return getItemCountWarning({ count, low_stock_threshold: threshold });
  }, [count, threshold]);

  const isItemAvailable = useMemo(
    () => !isItemOutOfStock({ count, preorder_allowed: isPreorderAllowed, type }),
    [count, isPreorderAllowed, type],
  );

  const warningIcon = useMemo(() => {
    if (!tooltipMessage) {
      return;
    }

    if (isDigitalItem) {
      return <InfoOutlinedIcon color="info" fontSize="small" />;
    }

    return isItemAvailable ? (
      <WarningAmberOutlinedIcon color="warning" fontSize="small" />
    ) : (
      <ErrorOutlineIcon color="error" fontSize="small" />
    );
  }, [tooltipMessage, isItemAvailable]);

  const label = useMemo(() => {
    switch (true) {
      case isDigitalItem:
        return null;
      case count === -1:
        return 'N/A';
      default:
        return (count || 0).toFixed();
    }
  }, [count, isDigitalItem]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, alignItems: 'center' }}>
      <Typography component="span" variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 14 }}>
        In stock{label ? `: ${label}` : ''}{' '}
        {tooltipMessage && (
          <Tooltip title={tooltipMessage} placement="top" arrow>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>{warningIcon}</Box>
          </Tooltip>
        )}
      </Typography>
      {isPreorderAllowed && (
        <Tooltip title={PREORDER_IS_ENABLED_FOR_CAMPAIGN_MESSAGE} placement="top" arrow>
          <BackorderChip />
        </Tooltip>
      )}
    </Box>
  );
};
